import React from 'react';
import { ChevronRight, ExternalLink, FileText, Layout, BookOpen, Code2, ShoppingBag, Shield, Home, Map } from 'lucide-react';
import { SeoHead } from '../components/SeoHead';
import { GUIDES, CATEGORY_COLOR } from './blog/guides';

/* ── Group guides by category ── */
const guidesByCategory = GUIDES.reduce((acc, g) => {
  if (!acc[g.category]) acc[g.category] = [];
  acc[g.category].push(g);
  return acc;
}, {});

/* ── Section block ── */
function SitemapSection({ icon: Icon, title, accent, children }) {
  return (
    <div className="mb-10">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-8 h-8 rounded-sm flex items-center justify-center shrink-0"
          style={{ background: `${accent}18`, border: `1px solid ${accent}35` }}>
          <Icon size={15} style={{ color: accent }} />
        </div>
        <h2 className="text-sm font-semibold uppercase tracking-[0.2em]"
          style={{ color: 'rgba(229,229,229,0.65)' }}>
          {title}
        </h2>
      </div>
      <div className="pl-11 space-y-1">
        {children}
      </div>
    </div>
  );
}

/* ── Single link row ── */
function SitemapLink({ href, label, desc, external, badge, badgeColor }) {
  return (
    <a
      href={href}
      target={external ? '_blank' : undefined}
      rel={external ? 'noreferrer' : undefined}
      className="group flex items-start justify-between gap-4 px-4 py-2.5 rounded-sm transition-all"
      style={{ border: '1px solid transparent' }}
      onMouseEnter={e => {
        e.currentTarget.style.background = 'rgba(201,168,76,0.04)';
        e.currentTarget.style.borderColor = 'rgba(201,168,76,0.15)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.background = 'transparent';
        e.currentTarget.style.borderColor = 'transparent';
      }}>
      <div className="flex items-center gap-3 min-w-0">
        <ChevronRight size={12} style={{ color: 'rgba(201,168,76,0.5)', flexShrink: 0 }}
          className="transition-transform group-hover:translate-x-0.5" />
        <div className="min-w-0">
          <span className="text-sm transition-colors" style={{ color: 'rgba(229,229,229,0.8)' }}>
            {label}
          </span>
          {desc && (
            <p className="text-xs mt-0.5 truncate" style={{ color: 'rgba(229,229,229,0.3)' }}>{desc}</p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {badge && (
          <span className="text-[9px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider"
            style={{ background: `${badgeColor || '#c9a84c'}18`, color: badgeColor || '#c9a84c', border: `1px solid ${badgeColor || '#c9a84c'}30` }}>
            {badge}
          </span>
        )}
        {external && <ExternalLink size={10} style={{ color: 'rgba(229,229,229,0.2)' }} />}
      </div>
    </a>
  );
}

export default function SitemapPage() {
  return (
    <div style={{ background: '#080808', color: '#e5e5e5', fontFamily: "'DM Sans', sans-serif", minHeight: '100vh' }}>
      <SeoHead page="sitemap" lang="it" theme="dark"
        customTitle="Mappa del Sito — Shangrilux"
        customDescription="Tutte le pagine pubbliche di Shangrilux: piattaforma, guide integrazioni, API, marketplace e pagine legali." />

      {/* Nav */}
      <nav className="sticky top-0 z-40 border-b px-4 sm:px-6 py-4 flex items-center justify-between"
        style={{ background: 'rgba(8,8,8,0.96)', backdropFilter: 'blur(20px)', borderColor: '#1a1a1a' }}>
        <div className="flex items-center gap-2 text-xs" style={{ color: 'rgba(229,229,229,0.4)' }}>
          <a href="/" className="hover:text-[#c9a84c] transition-colors" style={{ color: 'inherit' }}>Home</a>
          <ChevronRight size={11} />
          <span style={{ color: '#c9a84c' }}>Mappa del sito</span>
        </div>
        <a href="/#demo" className="text-xs px-4 py-2 rounded-sm font-medium"
          style={{ background: '#c9a84c', color: '#000' }}>Demo Gratuita</a>
      </nav>

      {/* Hero */}
      <div className="max-w-5xl mx-auto px-6 pt-14 pb-10">
        <div className="flex items-center gap-3 mb-3">
          <Map size={20} style={{ color: '#c9a84c' }} />
          <p className="text-xs uppercase tracking-[0.3em]" style={{ color: '#c9a84c' }}>Mappa del sito</p>
        </div>
        <h1 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 300, color: '#e5e5e5', lineHeight: 1.2, marginBottom: '12px' }}>
          Tutte le pagine di Shangrilux
        </h1>
        <p className="text-sm" style={{ color: 'rgba(229,229,229,0.45)', maxWidth: '480px', lineHeight: 1.7 }}>
          Una panoramica completa della piattaforma: pagine pubbliche, guide tecniche, documentazione API e risorse legali.
        </p>
        <div className="mt-6 h-px w-24" style={{ background: 'linear-gradient(to right, #c9a84c, transparent)' }} />
      </div>

      {/* Main content */}
      <div className="max-w-5xl mx-auto px-6 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-16">

          {/* Left column */}
          <div>
            {/* Piattaforma */}
            <SitemapSection icon={Layout} title="Piattaforma" accent="#c9a84c">
              <SitemapLink href="/" label="Home — Landing Page" desc="Hero, funzionalità, showcase, pricing, FAQ, demo" />
              <SitemapLink href="/#features" label="Funzionalità" desc="Chiavi digitali, smart home, booking, Guest App" />
              <SitemapLink href="/#for-who" label="Per chi è" desc="Hotel boutique, villa, property manager, resort" />
              <SitemapLink href="/#integrations" label="Integrazioni" desc="18 brand certificati — Nuki, KNX, Tuya, Home Assistant" />
              <SitemapLink href="/#pricing" label="Prezzi" desc="Piani Essenziale, Professional, Elite" />
              <SitemapLink href="/#faq" label="FAQ" desc="10 domande frequenti sulla piattaforma" />
              <SitemapLink href="/#demo" label="Demo Gratuita" desc="Ambiente completo 24h senza carta di credito" badge="PROVA" />
              <SitemapLink href="/pricing" label="Prezzi — Pagina completa" desc="Confronto piani, tabella funzionalità, FAQ prezzi" />
            </SitemapSection>

            {/* Risorse */}
            <SitemapSection icon={BookOpen} title="Guide & Blog" accent="#7c3aed">
              <SitemapLink href="/blog" label="Blog — Tutte le guide" desc="18 guide tecniche su serrature, automazioni e integrazioni" />
              <div className="mt-3 mb-2">
                {Object.entries(guidesByCategory).map(([cat, guides]) => (
                  <div key={cat} className="mb-4">
                    <p className="text-[10px] uppercase tracking-[0.2em] mb-1.5 px-4"
                      style={{ color: CATEGORY_COLOR[cat] || '#c9a84c' }}>
                      {cat}
                    </p>
                    {guides.map(g => (
                      <SitemapLink
                        key={g.slug}
                        href={`/blog/${g.slug}`}
                        label={g.brand}
                        desc={g.title.slice(0, 55) + (g.title.length > 55 ? '…' : '')}
                        badge={g.difficulty}
                        badgeColor={
                          g.difficulty === 'Avanzato' ? '#ef4444' :
                          g.difficulty === 'Intermedio' ? '#f59e0b' : '#10b981'
                        }
                      />
                    ))}
                  </div>
                ))}
              </div>
            </SitemapSection>
          </div>

          {/* Right column */}
          <div>
            {/* Developer */}
            <SitemapSection icon={Code2} title="Developer" accent="#0891b2">
              <SitemapLink href="/developers" label="API Documentation" desc="Autenticazione, endpoint, rate limiting, errori" />
              <SitemapLink href="/developers#authentication" label="Autenticazione API Key" desc="Header X-API-Key · formato ostx_..." />
              <SitemapLink href="/developers#rate-limits" label="Rate Limiting" desc="2.000 req/h (Professional) · 10.000 req/h (Elite)" />
              <SitemapLink href="/developers#errors" label="Gestione Errori" desc="Codici HTTP, struttura risposta JSON" />
              <SitemapLink href="/developers#bookings" label="Endpoint Prenotazioni" desc="GET/POST /v1/bookings · CRUD completo" />
              <SitemapLink href="/developers#properties" label="Endpoint Proprietà" desc="GET /v1/properties · lista e dettaglio" />
              <SitemapLink href="/developers#access" label="Endpoint Accessi" desc="GET /v1/access-log · comandi dispositivi" />
              <SitemapLink href="/developers#webhooks" label="Webhook & HMAC" desc="Firma HMAC-SHA256, verifica Python/Node.js, eventi" />
              <SitemapLink href="/developers#sdk" label="SDK Ufficiali" desc="Python (~6KB) e Node.js (~5KB) scaricabili" />
              <SitemapLink href="/sdk/shangrilux-python-sdk.py" label="SDK Python" desc="shangrilux-python-sdk.py · pip install requests" external badge="DOWNLOAD" />
              <SitemapLink href="/sdk/shangrilux-node-sdk.js" label="SDK Node.js" desc="shangrilux-node-sdk.js · nessuna dipendenza" external badge="DOWNLOAD" />
            </SitemapSection>

            {/* Marketplace */}
            <SitemapSection icon={ShoppingBag} title="Marketplace" accent="#059669">
              <SitemapLink href="/marketplace" label="Marketplace Integrazioni" desc="15+ integrazioni certificate: serrature, smart home, PMS" />
              <SitemapLink href="/marketplace#serrature" label="Serrature Smart" desc="Nuki, Tedee, TTLock, Yale, August, SALTO, dormakaba" />
              <SitemapLink href="/marketplace#smart-home" label="Smart Home" desc="KNX, Home Assistant, Tuya, Loxone, BTicino, Philips Hue" />
              <SitemapLink href="/marketplace#booking" label="Booking & PMS" desc="Apaleo, Mews — sincronizzazione bi-direzionale" />
              <SitemapLink href="/marketplace#automazioni" label="Automazioni" desc="Webhook, Make, Zapier, Google Sheets" />
            </SitemapSection>

            {/* Legale */}
            <SitemapSection icon={Shield} title="Legale & Informativa" accent="#6366f1">
              <SitemapLink href="/privacy" label="Privacy Policy" desc="GDPR — trattamento dati personali" />
              <SitemapLink href="/cookie" label="Cookie Policy" desc="Tipologie, finalità e gestione dei cookie" />
              <SitemapLink href="/terms" label="Termini di Servizio" desc="Condizioni d'uso della piattaforma Shangrilux" />
              <SitemapLink href="/sitemap" label="Mappa del sito" desc="Questa pagina" />
            </SitemapSection>

            {/* Accesso */}
            <SitemapSection icon={Home} title="Accesso Diretto" accent="#f59e0b">
              <SitemapLink href="/panel/login" label="Pannello Admin Tenant" desc="Accedi al tuo pannello operativo (richiede slug struttura)" />
              <SitemapLink href="/superadmin" label="SuperAdmin" desc="Area riservata — richiede credenziali SuperAdmin" />
              <SitemapLink href="/booking" label="Portale Prenotazioni" desc="Portale pubblico per gli ospiti (URL specifica per struttura)" />
              <SitemapLink href="mailto:info@shangrilux.com" label="info@shangrilux.com" desc="Supporto, partnership, richieste commerciali" external />
            </SitemapSection>
          </div>

        </div>

        {/* Footer stats */}
        <div className="mt-6 pt-8 border-t flex flex-wrap gap-6" style={{ borderColor: '#141414' }}>
          {[
            { n: '8', label: 'pagine pubbliche' },
            { n: '18', label: 'guide tecniche' },
            { n: '15+', label: 'integrazioni certificate' },
            { n: '3', label: 'pagine legali GDPR' },
          ].map(({ n, label }) => (
            <div key={label} className="text-center">
              <p style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '28px', fontWeight: 300, color: '#c9a84c', lineHeight: 1 }}>{n}</p>
              <p className="text-xs mt-1" style={{ color: 'rgba(229,229,229,0.3)' }}>{label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t py-10" style={{ borderColor: '#141414' }}>
        <div className="max-w-5xl mx-auto px-6 flex flex-col sm:flex-row flex-wrap items-center justify-between gap-4">
          <span style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '16px', fontWeight: 300, color: 'rgba(229,229,229,0.3)' }}>Shangrilux</span>
          <div className="flex flex-wrap gap-5 justify-center">
            {[['/', 'Home'], ['/pricing', 'Prezzi'], ['/blog', 'Guide'], ['/marketplace', 'Marketplace'], ['/developers', 'API']].map(([h, l]) => (
              <a key={h} href={h} className="text-xs hover:opacity-70 transition-opacity" style={{ color: 'rgba(229,229,229,0.3)' }}>{l}</a>
            ))}
          </div>
          <div className="flex flex-wrap gap-5 justify-center">
            {[['/privacy', 'Privacy'], ['/cookie', 'Cookie'], ['/terms', 'Termini'], ['/sitemap', 'Sitemap']].map(([h, l]) => (
              <a key={h} href={h} className="text-xs hover:opacity-70 transition-opacity" style={{ color: 'rgba(229,229,229,0.25)' }}>{l}</a>
            ))}
          </div>
          <p className="text-xs" style={{ color: 'rgba(229,229,229,0.2)' }}>
            © {new Date().getFullYear()} Shangrilux · Design by{' '}
            <a href="https://francescorusso.work" target="_blank" rel="noreferrer" className="hover:opacity-70 transition-opacity" style={{ color: 'rgba(201,168,76,0.5)' }}>Francesco Russo</a>
            {' · '}
            <a href="https://shangrilux.com" target="_blank" rel="noreferrer" className="hover:opacity-70 transition-opacity" style={{ color: 'rgba(201,168,76,0.5)' }}>Shangrilux</a>
          </p>
        </div>
      </footer>
    </div>
  );
}
